function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function inlineFormat(text: string): string {
  const escaped = escapeHtml(text);
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+?)\*/g, '<em>$1</em>');
}

/**
 * Lightweight markdown → HTML for AI briefs (headings, bold, lists).
 * Avoids extra parser deps that break CRA + TypeScript 4.9 resolution.
 */
export function formatAiMarkdown(markdown: string): string {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let inUl = false;
  let inOl = false;

  const closeLists = (): void => {
    if (inUl) {
      out.push('</ul>');
      inUl = false;
    }
    if (inOl) {
      out.push('</ol>');
      inOl = false;
    }
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trimEnd();
    if (trimmed.trim() === '') {
      closeLists();
      continue;
    }

    const h3 = trimmed.match(/^###\s+(.+)$/);
    if (h3) {
      closeLists();
      out.push(`<h3>${inlineFormat(h3[1])}</h3>`);
      continue;
    }

    const h2 = trimmed.match(/^##\s+(.+)$/);
    if (h2) {
      closeLists();
      out.push(`<h2>${inlineFormat(h2[1])}</h2>`);
      continue;
    }

    const h1 = trimmed.match(/^#\s+(.+)$/);
    if (h1) {
      closeLists();
      out.push(`<h1>${inlineFormat(h1[1])}</h1>`);
      continue;
    }

    const ul = trimmed.match(/^(\s*)[*+\-]\s+(.+)$/);
    if (ul) {
      if (inOl) {
        out.push('</ol>');
        inOl = false;
      }
      if (!inUl) {
        out.push('<ul>');
        inUl = true;
      }
      const depth = Math.floor(ul[1].replace(/\t/g, '  ').length / 2);
      const indentClass = depth > 0 ? ` class="ml-${Math.min(depth * 4, 8)}"` : '';
      out.push(`<li${indentClass}>${inlineFormat(ul[2])}</li>`);
      continue;
    }

    const ol = trimmed.match(/^(\s*)\d+\.\s+(.+)$/);
    if (ol) {
      if (inUl) {
        out.push('</ul>');
        inUl = false;
      }
      if (!inOl) {
        out.push('<ol>');
        inOl = true;
      }
      out.push(`<li>${inlineFormat(ol[2])}</li>`);
      continue;
    }

    closeLists();
    out.push(`<p>${inlineFormat(trimmed)}</p>`);
  }

  closeLists();
  return out.join('\n');
}

/** Matches backend `recordEmailAssistantInChat` user message format. */
export function formatEmailAssistantPromptMessage(
  promptName: string | undefined,
  subject: string | undefined,
  emailId: number | undefined,
  promptText: string,
  composeSessionId?: string,
): string {
  const promptLabel = promptName?.trim() || 'AI Assistant';
  const subj = subject?.trim() || '(no subject)';
  const composeContext = Boolean(composeSessionId?.trim()) || emailId == null;
  let userMessage = `**${promptLabel}**`;
  userMessage += composeContext ? ` — Compose: "${subj}"` : ` — Email: "${subj}"`;
  const resolvedPrompt = promptText.trim();
  if (resolvedPrompt) {
    userMessage += `\n\n---\n\n${resolvedPrompt}`;
  }
  return userMessage;
}
